import { UploadStore } from "./UploadStore";
import { AttachmentDataModel } from "../../models/data/AttachmentDataModel";
import { ConceptAttachmentStore } from "../stores/ConceptAttachment/ConceptAttachmentStore";
import { IHubSubcription, MessageHubContext, MessageHubHandler } from "../notification/MessageHubHandler";
import { MessageHubOwner } from "../messaging/MessageHubOwner";
import { MessageType } from "../../models/message/IServerMessage";
import { ConceptAttachmentSaveActionResponse } from "../../models/communication/actions/ConceptAttachmentSaveAction";
import { ConceptAttachmentUploadCompleteActionResponse } from "../../models/communication/actions/ConceptAttachmentUploadCompleteAction";
import { ConceptAttachmentNotifyResponse } from "../../models/communication/actions/ConceptAttachmentNotify";
import { LogCollector } from "../logger/LogCollector";
import { MessageService } from "../messaging/MessageService";

const serverURL = process.env.REACT_APP_INTEGRATION_URL;

export interface UploadServiceProps {
    uploadStore: UploadStore;
    attachmentStore: ConceptAttachmentStore;
    maxFileSize: number;
    fileTypesAllowed?: string;

    disableNotification?: boolean;
}

export enum FailureReasons {
    NoneSpecified = 0,
    FileTooBig = 1,
    FileMustBeImage = 2,
    MaxUploadReached = 3,

    NotEnoughSpaceToUpload = 10
}

export class UploadService {
    props: UploadServiceProps;

    uploadModifier: number = 1;
    currentHandlers: MessageHubHandler[] = [];

    constructor(props: UploadServiceProps) {
        this.props = props;
    }

    isStorageConfigured() {
        return !serverURL ? false : true;
    }


    async uploadAttachment(attachment: AttachmentDataModel, uploadingFile: File) {

        return new Promise(async (resolve, reject) => {

            //  return;
            if (!uploadingFile) {
                reject(FailureReasons.NoneSpecified);
                return;
            }

            if (this.props.fileTypesAllowed) {
                if (!uploadingFile.type) {
                    reject(FailureReasons.FileMustBeImage);
                    return;
                }

                if (!(uploadingFile.type.toLowerCase().indexOf(this.props.fileTypesAllowed.toLowerCase()) > -1)) {
                    reject(FailureReasons.FileMustBeImage);
                    return;
                }
            }

            const updateFileSize = uploadingFile.size / 1024 / 1024;
            LogCollector.LogMessage(updateFileSize);
            const maxFileSize = this.props.maxFileSize;

            if (updateFileSize > maxFileSize) {
                reject(FailureReasons.FileTooBig);
                return;
            }

            LogCollector.LogMessage("...................... ValidationStep!");
            resolve(true);
        }).then(() => {
            LogCollector.LogMessage("...................... Calculating position");
            this.props.attachmentStore.calculateAttachmentOrder(attachment);
        }).then(() => {

            return new Promise(async (resolve, reject) => {
                LogCollector.LogMessage("...................... NotifyStep!");

                const notifyListener = MessageHubContext().ListenMessage(MessageHubOwner, {
                    MessageType: MessageType.ConceptAttachmentNotified, OnReceive: (response: ConceptAttachmentNotifyResponse) => {

                        notifyListener.Dispose();

                        if (response.attachmentID === attachment.attachmentID) {
                            if (response.hasEnoughSpace) {
                                resolve(true);
                            }
                            else {
                                reject(FailureReasons.NotEnoughSpaceToUpload);
                            }
                        }
                        else {
                            reject(FailureReasons.NoneSpecified);
                        }
                    }
                } as IHubSubcription);

                this.currentHandlers.push(notifyListener);

                await this.props.attachmentStore.notifyUpload(attachment);
            }).then(() => {
                LogCollector.LogMessage("...................... UploadToServer!");
                return this.uploadToServer(uploadingFile, attachment.attachmentID);
            }).then(() => {

                return new Promise(async (resolve, reject) => {
                    LogCollector.LogMessage("...................... SaveAttachement!");

                    if (!this.props.attachmentStore.syncWithServer) {
                        LogCollector.LogMessage("...................... SkipSaveAttachement!");
                        resolve(true);
                        return;
                    }

                    const saveListener = MessageHubContext().ListenMessage(MessageHubOwner, {
                        MessageType: MessageType.ConceptAttachmentSaved, OnReceive: (response: ConceptAttachmentSaveActionResponse) => {

                            saveListener.Dispose();

                            if (response.attachmentID === attachment.attachmentID) {
                                resolve(true);
                            }
                            else {
                                reject(FailureReasons.NoneSpecified);
                            }
                        }
                    } as IHubSubcription);

                    this.currentHandlers.push(saveListener);

                    await this.props.attachmentStore.saveNewAttachement(attachment);
                });
            }).then(() => {
                return new Promise(async (resolve, reject) => {
                    LogCollector.LogMessage("...................... NotifyComplete!");

                    if (this.props.attachmentStore.syncWithServer) {
                        LogCollector.LogMessage("...................... SkipNotifyComplete!");
                        resolve(true);
                        return;
                    }

                    const completeListener = MessageHubContext().ListenMessage(MessageHubOwner, {
                        MessageType: MessageType.ConceptAttachmentUploadCompleted, OnReceive: (response: ConceptAttachmentUploadCompleteActionResponse) => {

                            completeListener.Dispose();

                            if (response.attachmentID === attachment.attachmentID) {
                                resolve(true);
                            }
                            else {
                                reject(FailureReasons.NoneSpecified);
                            }
                        }
                    } as IHubSubcription);

                    this.currentHandlers.push(completeListener);

                    await this.props.attachmentStore.completeUpload(attachment);
                });

            }).then(() => {
                LogCollector.LogMessage("...................... AddAttachement!");
                if (this.props.disableNotification) {
                    return;
                }

                this.props.attachmentStore.addNewAttachment(attachment);
            });

            //MessageHubContext().ListenMessage(MessageHubOwner, { MessageType: MessageType.ConceptAttachmentSaved, OnReceive: onConceptUpdate } as IHubSubcription)
            //await this.props.attachmentStore.addNewAttachment(attachment);
        });
    }

    // Uses XMLHttpRequest rather than fetch on purpose: fetch has no upload progress
    // event, so switching to it would silently kill the progress bar. Request streaming
    // is Chrome-only and needs HTTP/2 plus duplex: "half", which is not a viable swap.
    async uploadToServer(uploadingFile: File, attachmentID: string) {
        return new Promise((resolve, reject) => {

            const uploadUrl = serverURL + "/secure/storage/upload?attachmentID=" + encodeURIComponent(attachmentID);

            const request = new XMLHttpRequest();
            request.open("POST", uploadUrl);

            // Read the token at send time so a renewal mid-session is picked up.
            request.setRequestHeader("Authorization", `Bearer ${MessageService.accessToken}`);

            request.upload.onprogress = (progress: ProgressEvent) => {
                if (!this.props.uploadStore || !progress.lengthComputable) {
                    return;
                }

                const percent = (progress.loaded / progress.total * 100.0) * this.uploadModifier;

                LogCollector.LogMessage(percent);
                this.props.uploadStore.updateProgress(percent);
            };

            request.onload = () => {
                if (request.status >= 200 && request.status < 300) {
                    resolve(true);
                    return;
                }

                LogCollector.LogError(`Upload failed with status ${request.status}`);
                reject(FailureReasons.NoneSpecified);
            };

            request.onerror = () => {
                LogCollector.LogError("Upload failed to reach the server.");
                reject(FailureReasons.NoneSpecified);
            };

            request.onabort = () => reject(FailureReasons.NoneSpecified);

            // The server derives the stored name from the notify registration, so the
            // filename here is only ever used for the multipart part header.
            const body = new FormData();
            body.append("file", uploadingFile, uploadingFile.name);

            request.send(body);
        });
    }


    dispose() {

        if (this.currentHandlers && this.currentHandlers.length > 0) {
            this.currentHandlers.forEach(handler => handler.Dispose());
            this.currentHandlers = [];
        }
    }
}